import { useState, useCallback } from "react";
import { TopBar } from "../components/layout/TopBar";
import { SectionLabel } from "../components/shared/SectionLabel";
import { EmptyState } from "../components/shared/EmptyState";
import * as ui from "../styles/ui";
import { api, type InstantiateResult, type DriftResult } from "../api/client";

interface InstanceRow {
  family: string;
  bindings: Record<string, string>;
}

function emptyRow(variables: string[]): InstanceRow {
  return {
    family: "",
    bindings: Object.fromEntries(variables.map((v) => [v, ""])),
  };
}

export function TemplateFamiliesPage() {
  const [templatePath, setTemplatePath] = useState("templates/");
  const [variables, setVariables] = useState<string[] | null>(null);
  const [loadingVars, setLoadingVars] = useState(false);
  const [rows, setRows] = useState<InstanceRow[]>([]);
  const [result, setResult] = useState<InstantiateResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // Drift checker
  const [driftPath, setDriftPath] = useState("");
  const [drift, setDrift] = useState<DriftResult | null>(null);
  const [driftError, setDriftError] = useState<string | null>(null);
  const [driftLoading, setDriftLoading] = useState(false);

  const loadVariables = useCallback(async () => {
    setLoadingVars(true);
    setError(null);
    setResult(null);
    try {
      const res = await api.templateFamilies.variables(templatePath.trim());
      setVariables(res.variables);
      setRows([emptyRow(res.variables)]);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load template");
      setVariables(null);
      setRows([]);
    } finally {
      setLoadingVars(false);
    }
  }, [templatePath]);

  const addRow = useCallback(() => {
    setRows((prev) => [...prev, emptyRow(variables ?? [])]);
  }, [variables]);

  const removeRow = useCallback((idx: number) => {
    setRows((prev) => prev.filter((_, i) => i !== idx));
  }, []);

  const updateRow = useCallback((idx: number, patch: Partial<InstanceRow>) => {
    setRows((prev) => prev.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
  }, []);

  const updateBinding = useCallback(
    (idx: number, variable: string, value: string) => {
      setRows((prev) =>
        prev.map((r, i) =>
          i === idx
            ? { ...r, bindings: { ...r.bindings, [variable]: value } }
            : r,
        ),
      );
    },
    [],
  );

  const canSubmit = rows.length > 0 && rows.every((r) => r.family.trim());

  const instantiate = useCallback(async () => {
    if (!canSubmit || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      const res = await api.templateFamilies.instantiate({
        templatePath: templatePath.trim(),
        instances: rows.map((r) => ({
          family: r.family.trim(),
          bindings: r.bindings,
        })),
      });
      setResult(res);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Instantiation failed");
    } finally {
      setSubmitting(false);
    }
  }, [canSubmit, submitting, templatePath, rows]);

  const checkDrift = useCallback(async () => {
    if (!driftPath.trim()) return;
    setDriftLoading(true);
    setDriftError(null);
    setDrift(null);
    try {
      const res = await api.templateFamilies.drift(driftPath.trim());
      setDrift(res);
    } catch (err) {
      setDriftError(err instanceof Error ? err.message : "Drift check failed");
    } finally {
      setDriftLoading(false);
    }
  }, [driftPath]);

  return (
    <>
      <TopBar title="template families" />
      <div style={ui.pageContent}>
        {/* ── Instantiate ─────────────────────────────────────────── */}
        <SectionLabel>canonical template</SectionLabel>
        <div style={styles.row}>
          <input
            type="text"
            value={templatePath}
            onChange={(e) => setTemplatePath(e.target.value)}
            placeholder="templates/index-methodology.md"
            data-testid="template-path"
            style={styles.input}
          />
          <button
            type="button"
            onClick={loadVariables}
            disabled={loadingVars}
            style={styles.btn}
          >
            {loadingVars ? "loading…" : "load variables"}
          </button>
        </div>

        {error && (
          <div style={styles.error} data-testid="tf-error">
            {error}
          </div>
        )}

        {variables && (
          <>
            <div style={styles.varsLine}>
              {variables.length > 0 ? (
                <>
                  variables:{" "}
                  {variables.map((v) => (
                    <code key={v} style={styles.var}>{`{{${v}}}`}</code>
                  ))}
                </>
              ) : (
                "this template declares no variables"
              )}
            </div>

            <SectionLabel>instances</SectionLabel>
            <table style={ui.table}>
              <thead>
                <tr>
                  <th style={{ ...ui.th, width: 160 }}>family slug</th>
                  {variables.map((v) => (
                    <th key={v} style={ui.th}>
                      {v}
                    </th>
                  ))}
                  <th style={{ ...ui.th, width: 32 }} />
                </tr>
              </thead>
              <tbody>
                {rows.map((r, idx) => (
                  <tr key={idx} style={ui.row}>
                    <td style={ui.td}>
                      <input
                        type="text"
                        value={r.family}
                        onChange={(e) =>
                          updateRow(idx, { family: e.target.value })
                        }
                        placeholder="gold-emea"
                        aria-label={`family for instance ${idx + 1}`}
                        style={styles.cellInput}
                      />
                    </td>
                    {variables.map((v) => (
                      <td key={v} style={ui.td}>
                        <input
                          type="text"
                          value={r.bindings[v] ?? ""}
                          onChange={(e) =>
                            updateBinding(idx, v, e.target.value)
                          }
                          aria-label={`${v} for instance ${idx + 1}`}
                          style={styles.cellInput}
                        />
                      </td>
                    ))}
                    <td style={ui.td}>
                      <button
                        type="button"
                        onClick={() => removeRow(idx)}
                        aria-label={`remove instance ${idx + 1}`}
                        style={styles.removeBtn}
                      >
                        ×
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            <div style={styles.row}>
              <button type="button" onClick={addRow} style={styles.linkBtn}>
                + add instance
              </button>
              <button
                type="button"
                onClick={instantiate}
                disabled={!canSubmit || submitting}
                data-testid="tf-instantiate"
                style={styles.primaryBtn}
              >
                {submitting
                  ? "creating…"
                  : `instantiate ${rows.length} doc${rows.length !== 1 ? "s" : ""}`}
              </button>
            </div>
          </>
        )}

        {result && (
          <div style={styles.resultPanel} data-testid="tf-result">
            <div style={styles.resultHead}>
              created {result.created} of {result.results.length}
            </div>
            <table style={ui.table}>
              <thead>
                <tr>
                  <th style={ui.th}>path</th>
                  <th style={{ ...ui.th, width: 90 }}>status</th>
                  <th style={ui.th}>detail</th>
                </tr>
              </thead>
              <tbody>
                {result.results.map((r) => (
                  <tr key={r.path} style={ui.row}>
                    <td style={styles.mono}>{r.path}</td>
                    <td style={styles.mono}>
                      <span
                        style={
                          r.status === "created"
                            ? styles.ok
                            : r.status === "failed"
                              ? styles.err
                              : styles.mute
                        }
                      >
                        {r.status}
                      </span>
                    </td>
                    <td style={styles.mono}>{r.detail ?? "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* ── Drift checker ───────────────────────────────────────── */}
        <div style={styles.driftSection}>
          <SectionLabel>template drift</SectionLabel>
          <div style={styles.row}>
            <input
              type="text"
              value={driftPath}
              onChange={(e) => setDriftPath(e.target.value)}
              placeholder="methodologies/gold-emea/v0.1.md"
              data-testid="drift-path"
              style={styles.input}
            />
            <button
              type="button"
              onClick={checkDrift}
              disabled={driftLoading}
              style={styles.btn}
            >
              {driftLoading ? "checking…" : "check drift"}
            </button>
          </div>

          {driftError && (
            <div style={styles.error} data-testid="drift-error">
              {driftError}
            </div>
          )}

          {drift && drift.templateSource === null && (
            <EmptyState
              title="not a template instance"
              body="this doc was not instantiated from a template"
            />
          )}

          {drift && drift.templateSource !== null && (
            <div data-testid="drift-result">
              <div style={styles.driftHead}>
                {drift.drifted ? (
                  <span style={styles.err}>
                    drifted — {drift.changedLines} changed line
                    {drift.changedLines !== 1 ? "s" : ""} vs{" "}
                    {drift.templateSource}
                  </span>
                ) : (
                  <span style={styles.ok}>
                    in sync with {drift.templateSource}
                  </span>
                )}
              </div>
              {drift.drifted && (
                <pre style={styles.diff}>
                  {drift.diff
                    .filter((d) => d.type !== "same")
                    .map((d, i) => (
                      <div
                        key={i}
                        style={
                          d.type === "add" ? styles.diffAdd : styles.diffDel
                        }
                      >
                        {d.type === "add" ? "+ " : "- "}
                        {d.text}
                      </div>
                    ))}
                </pre>
              )}
            </div>
          )}
        </div>
      </div>
    </>
  );
}

const styles: Record<string, React.CSSProperties> = {
  row: { display: "flex", alignItems: "center", gap: 8, margin: "8px 0 16px" },
  input: {
    flex: 1,
    border: "1px solid var(--comp-rule)",
    padding: "5px 10px",
    fontFamily: "var(--font-mono)",
    fontSize: 13,
    color: "var(--fg-1)",
    background: "transparent",
    outline: "none",
  },
  cellInput: {
    width: "100%",
    border: "1px solid var(--comp-rule)",
    padding: "3px 6px",
    fontFamily: "var(--font-mono)",
    fontSize: 12,
    color: "var(--fg-1)",
    background: "transparent",
    outline: "none",
  },
  btn: {
    padding: "5px 12px",
    fontFamily: "var(--font-mono)",
    fontSize: 11,
    cursor: "pointer",
    background: "transparent",
    color: "var(--fg-1)",
    border: "1px solid var(--comp-rule)",
  },
  primaryBtn: {
    padding: "5px 14px",
    fontFamily: "var(--font-mono)",
    fontSize: 11,
    fontWeight: 500,
    cursor: "pointer",
    background: "var(--comp-ink)",
    color: "var(--adj-white)",
    border: "1px solid var(--comp-ink)",
    marginLeft: "auto",
  },
  linkBtn: {
    fontFamily: "var(--font-mono)",
    fontSize: 11,
    color: "var(--fg-3)",
    textDecoration: "underline",
    padding: 0,
    border: "none",
    background: "none",
    cursor: "pointer",
  },
  removeBtn: {
    fontFamily: "var(--font-mono)",
    fontSize: 14,
    color: "var(--fg-3)",
    border: "none",
    background: "none",
    cursor: "pointer",
    lineHeight: 1,
  },
  varsLine: {
    fontFamily: "var(--font-mono)",
    fontSize: 11,
    color: "var(--fg-3)",
    margin: "4px 0 16px",
    display: "flex",
    flexWrap: "wrap" as const,
    gap: 6,
    alignItems: "center",
  },
  var: {
    fontFamily: "var(--font-mono)",
    fontSize: 11,
    color: "var(--fg-1)",
    background: "var(--comp-hover)",
    padding: "1px 5px",
  },
  error: {
    color: "var(--comp-salmon)",
    fontSize: 12,
    margin: "4px 0",
    fontFamily: "var(--font-mono)",
  },
  resultPanel: { marginTop: 16 },
  resultHead: {
    fontFamily: "var(--font-mono)",
    fontSize: 12,
    fontWeight: 600,
    marginBottom: 8,
  },
  mono: {
    padding: "var(--row-pad)",
    fontFamily: "var(--font-mono)",
    fontSize: 11,
    color: "var(--fg-2)",
  },
  ok: { color: "var(--fg-1)", fontWeight: 600 },
  err: { color: "var(--comp-salmon)", fontWeight: 600 },
  mute: { color: "var(--fg-mute)" },
  driftSection: {
    marginTop: 32,
    borderTop: "1px solid var(--comp-rule)",
    paddingTop: 16,
  },
  driftHead: { fontFamily: "var(--font-mono)", fontSize: 12, margin: "8px 0" },
  diff: {
    fontFamily: "var(--font-mono)",
    fontSize: 11,
    background: "var(--comp-hover)",
    padding: 10,
    overflowX: "auto" as const,
    whiteSpace: "pre-wrap" as const,
    margin: 0,
  },
  diffAdd: { color: "var(--fg-1)" },
  diffDel: { color: "var(--comp-salmon)" },
};
